import { Box, Divider, Typography } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import React from "react";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";

export const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.primary.text,
    maxWidth: 340,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.pxToRem(16),
  },
}));

const HeatMap = ({ data, theme, mode }) => {
  const categories = [
    "7:00 AM",
    "7:30 AM",
    "8:00 AM",
    "8:30 AM",
    "9:00 AM",
    "9:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "1:00 PM",
    "1:30 PM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
    "4:30 PM",
    "5:00 PM",
    "5:30 PM",
    "6:00 PM",
    "6:30 PM",
    "7:00 PM",
    "7:30 PM",
    "8:00 PM",
    "8:30 PM",
    "9:00 PM",
    "9:30 PM",
  ];

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const formatData = (data) => {
    let formattedData = [];
    categories.forEach((category, index) => {
      const timeSeries = days.map((day) => {
        const dayData = data.filter((item) => item.DayOfWeek === day);
        const timeData = dayData.find((item) => {
          const itemStart =
            parseInt(item.StartTime.split(":")[0], 10) * 60 +
            parseInt(item.StartTime.split(":")[1], 10);
          const itemEnd =
            parseInt(item.EndTime.split(":")[0], 10) * 60 +
            parseInt(item.EndTime.split(":")[1], 10);
          const slotStart = index * 30 + 420; // 7:00 AM is 420 minutes from midnight
          const slotEnd = slotStart + 30;
          return itemStart <= slotStart && itemEnd >= slotEnd;
        });

        let label = "";
        let availability = 0;

        if (timeData) {
          const { IsAvailablePhone, IsAvailableVirtual, IsAvailableInPerson } =
            timeData;
          if (IsAvailablePhone && !IsAvailableVirtual && !IsAvailableInPerson) {
            availability = 1;
            label = "P";
          } else if (
            !IsAvailablePhone &&
            IsAvailableVirtual &&
            !IsAvailableInPerson
          ) {
            availability = 2;
            label = "V";
          } else if (
            !IsAvailablePhone &&
            !IsAvailableVirtual &&
            IsAvailableInPerson
          ) {
            availability = 3;
            label = "I";
          } else if (
            IsAvailablePhone &&
            IsAvailableVirtual &&
            !IsAvailableInPerson
          ) {
            availability = 4;
            label = "P, V";
          } else if (
            IsAvailablePhone &&
            !IsAvailableVirtual &&
            IsAvailableInPerson
          ) {
            availability = 5;
            label = "P, I";
          } else if (
            !IsAvailablePhone &&
            IsAvailableVirtual &&
            IsAvailableInPerson
          ) {
            availability = 6;
            label = "V, I";
          } else if (
            IsAvailablePhone &&
            IsAvailableVirtual &&
            IsAvailableInPerson
          ) {
            availability = 7;
            label = "P, V, I";
          } else {
            label = "";
          }
        }

        return {
          x: day,
          y: availability,
          label: label,
        };
      });
      formattedData.push({ name: category, data: timeSeries });
    });

    return formattedData;
  };

  function getColor(dataY) {
    const switchData =
      dataY === 0 ? (data[0] && data[0].Availability) || "Unavailable" : "";

    switch (switchData) {
      case "Flexible":
        return theme.palette.primary.main;
      case "Some Availability":
        return theme.palette.primary.main;
      case "Limited Availability":
        return theme.palette.primary.main;
      case "Afternoon Availability":
        return theme.palette.primary.main;
      case "With Notice":
        return theme.palette.primary.main;
      case "Unavailable":
        return "transparent"; // No border for unavailable
      default:
        return dataY === 0 ? "transparent" : theme.palette.primary.main;
    }
  }

  // Function to determine if cell should have background color
  function hasAvailability(dataY) {
    // If dataY is 1-7, it means there's some form of availability (P, V, I or combination)
    return dataY > 0;
  }

  // Function to get the availability text for cells without P, V, I
  function getAvailabilityText(dataY) {
    if (dataY > 0) {
      return ""; // For cells with P, V, I availability, return empty string
    }

    // For cells without P, V, I, return the availability text
    return (data[0] && data[0].Availability) || "Unavailable";
  }

  function getTooltip(seriesData, serie, seriesIndex, dataItem) {
    //if(dataItem.y === 0)
    //  return data[0].Availability || "Unavailable"

    const day = "On " + dataItem.x + "\n";

    const timeFrom = "From " + serie.name + " To ";

    const timeTo =
      seriesData.length - 1 === seriesIndex
        ? "10:00 PM\n"
        : seriesData[seriesIndex + 1].name + "\n";

    let label = (data[0] && data[0].Availability) || "Unavailable";

    switch (dataItem.y) {
      case 1:
        label = "Phone only.";
        break;
      case 2:
        label = "Virtual only.";
        break;
      case 3:
        label = "In Person only.";
        break;
      case 4:
        label = "Phone and Virtual.";
        break;
      case 5:
        label = "Phone and In Person.";
        break;
      case 6:
        label = "Virtual and In Person.";
        break;
      case 7:
        label = "Phone, Virtual, and In Person.";
        break;
      default:
        label = (data[0] && data[0].Availability) || "Unavailable";
    }

    const availabilityTooltip = day + timeFrom + timeTo + label;

    return availabilityTooltip;
  }

  const series = formatData(data);

  const headers = [
    "",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  return (
    <Box height="100%" width="98%">
      <Grid
        container
        alignItems="center"
        spacing={1}
        columns={16}
        sx={{
          overflowY: "scroll",
          px: ".8rem",
        }}
      >
        {headers.map((day) => (
          <Grid size={{ xs: 2 }} key={day}>
            <Box
              //bgcolor="red"
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="40px"
              //borderColor="red"
              //border="3px solid"
              //borderRadius="12px"
            >
              <Typography>{day}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Divider
        sx={{
          my: ".5rem",
        }}
      />
      <Box
        //bgcolor="pink"
        sx={{
          px: ".8rem",
          overflowY: "scroll",
          flex: 1,
          height: "95%",
          //maxHeight: "400px",
          display: "flex",
          width: "100%",
        }}
      >
        <Grid
          sx={{
            flex: 1,
          }}
          container
          alignItems="center"
          spacing={1}
          columns={16}
        >
          {series.map((serie, seriesIndex) => (
            <Grid
              container
              alignItems="center"
              spacing={1}
              columns={16}
              size={{ xs: 16 }}
              key={serie.name + "--" + seriesIndex}
            >
              <Grid size={{ xs: 2 }}>
                <Box
                  //bgcolor="blue"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="35px"
                  width="100%"
                >
                  <Typography>{serie.name}</Typography>
                </Box>
              </Grid>
              {serie.data.map((dataItem, dataIndex) => (
                <Grid
                  size={{ xs: 2 }}
                  key={dataIndex + "-" + dataItem.x + "-" + dataItem.y}
                >
                  <HtmlTooltip
                    followCursor
                    title={
                      <Box p=".5rem" style={{ whiteSpace: "pre-line" }}>
                        <Typography color="inherit">
                          {getTooltip(series, serie, seriesIndex, dataItem)}
                          {/*"On "+dataItem.x+"\n"}
                            {"From "+serie.name + " To "+series[(seriesIndex+1)].name */}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box
                      bgcolor={
                        hasAvailability(dataItem.y)
                          ? theme.palette.primary.main
                          : theme.palette.background.default
                      }
                      border={
                        !hasAvailability(dataItem.y) && dataItem.y === 0
                          ? "2px solid"
                          : "none"
                      }
                      borderColor={getColor(dataItem.y)}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="35px"
                      width="100%"
                      borderRadius="12px"
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: dataItem.y === 0 ? "0.65rem" : "inherit",
                          fontWeight: dataItem.y === 0 ? "medium" : "inherit",
                        }}
                      >
                        {dataItem.y > 0
                          ? dataItem.label
                          : getAvailabilityText(dataItem.y)}
                      </Typography>
                    </Box>
                  </HtmlTooltip>
                </Grid>
              ))}
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default HeatMap;
